import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

interface UseSearchParamIdReturn {
  value: number | undefined;
  setParam: (id: number) => void;
  clearParam: () => void;
  toggleParam: (id: number) => void;
}

export const useSearchParamId = (paramKey: string): UseSearchParamIdReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const value = useMemo<number | undefined>(() => {
    const stringId = searchParams.get(paramKey);
    if (stringId) {
      const parsedId = Number(stringId);
      if (!isNaN(parsedId)) {
        return parsedId;
      }
    }
  }, [paramKey, searchParams]);

  const setParam = useCallback(
    (id: number) => {
      setSearchParams({ [paramKey]: id.toString() });
    },
    [paramKey, setSearchParams],
  );

  const clearParam = useCallback(() => {
    searchParams.delete(paramKey);
    setSearchParams(searchParams);
  }, [paramKey, searchParams, setSearchParams]);

  const toggleParam = useCallback(
    (id: number): void => {
      if (value === id) {
        clearParam();
      } else {
        setParam(id);
      }
    },
    [value, setParam, clearParam],
  );

  return {
    value,
    setParam,
    clearParam,
    toggleParam,
  };
};
